import { ThirdPartyResponseStatus } from '@risksmart-app/domain/src/types/consts/third-party-response-status';
import type { ThirdPartyResponseByIdResponseRow } from '@risksmart-app/drizzle/src/types';
import { TppGetResponseByIdDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createSubscriptionHook } from '../utils/hook-factories';
import { toIso, toIsoOrNull } from './tppResponseMappers';
import type {
  SubscriptionCompatibilityResult,
  TransportCompatibility,
} from './transportCompatibility';

// Subscription holding pattern (RSP-4320): the tRPC branch polls in place of
// the live socket, at the legacy 30s cadence.
const RESPONSE_POLL_INTERVAL_MS = 30_000;

/**
 * Reshapes a tRPC portal by-id row into the legacy `tppGetResponseById`
 * subscription shape (adds Schema/UISchema + files over the list shape) so the
 * questionnaire page reads identical data on both flag paths. The by-id query
 * config also selects `questionnaireTemplateVersion.Status`, which the legacy
 * subscription never exposed — it is dropped here to keep the shape exact.
 */
const toGraphQLResponse = (row: ThirdPartyResponseByIdResponseRow) => ({
  __typename: 'third_party_response' as const,
  Id: row.Id,
  Status: row.Status,
  ResponseData: row.ResponseData,
  ParentId: row.ParentId,
  StartDate: toIsoOrNull(row.StartDate),
  ExpiresAt: toIsoOrNull(row.ExpiresAt),
  QuestionnaireTemplateVersionId: row.QuestionnaireTemplateVersionId,
  ModifiedByUser: row.ModifiedByUser,
  ModifiedAtTimestamp: toIso(row.ModifiedAtTimestamp),
  CreatedByUser: row.CreatedByUser,
  CreatedAtTimestamp: toIso(row.CreatedAtTimestamp),
  questionnaireTemplateVersion: row.questionnaireTemplateVersion
    ? {
        __typename: 'questionnaire_template_version' as const,
        Id: row.questionnaireTemplateVersion.Id,
        Version: row.questionnaireTemplateVersion.Version,
        Schema: row.questionnaireTemplateVersion.Schema,
        UISchema: row.questionnaireTemplateVersion.UISchema,
        parent: row.questionnaireTemplateVersion.parent
          ? {
              __typename: 'questionnaire_template' as const,
              Id: row.questionnaireTemplateVersion.parent.Id,
              Title: row.questionnaireTemplateVersion.parent.Title,
            }
          : undefined,
      }
    : undefined,
  files: row.files.map((relation) => ({
    __typename: 'relation_file' as const,
    file: relation.file
      ? {
          __typename: 'file' as const,
          ContentType: relation.file.ContentType,
          FileName: relation.file.FileName,
          FileSize: relation.file.FileSize,
          Id: relation.file.Id,
          CreatedAtTimestamp: toIso(relation.file.CreatedAtTimestamp),
          CreatedByUser: relation.file.CreatedByUser,
          ModifiedAtTimestamp: toIso(relation.file.ModifiedAtTimestamp),
          ModifiedByUser: relation.file.ModifiedByUser,
          Meta: relation.file.Meta,
        }
      : undefined,
  })),
});

const mapResponse = (row: ThirdPartyResponseByIdResponseRow) => ({
  third_party_response_by_pk: toGraphQLResponse(row),
});

export type TppResponseByIdItem = TransportCompatibility<
  ReturnType<typeof toGraphQLResponse>
>;
export type TppResponseByIdResult = SubscriptionCompatibilityResult<
  ReturnType<typeof mapResponse>,
  'third_party_response_by_pk'
>;

/**
 * Flag-gated single questionnaire response (RSP-6570). Built on the portal's
 * `createSubscriptionHook`: flag off → the legacy Apollo `tppGetResponseById`
 * subscription; flag on → the tRPC `portal.thirdPartyResponses.getById` query,
 * polled while the response is not yet `completed` (a data-driven poll like the
 * change-request feed) so a status flip while the respondent edits
 * (Recalled/Rejected) is picked up without a live socket. Both branches return
 * `{ third_party_response_by_pk }` so the questionnaire page never learns which
 * path executed.
 */
const useTppResponseByIdHook = createSubscriptionHook<
  { id: string },
  ThirdPartyResponseByIdResponseRow,
  TppResponseByIdResult
>({
  trpcQueryOptions: (trpc, { id }) =>
    trpc.frontend.portal.thirdPartyResponses.getById.queryOptions({ id }),
  mapTrpcDataToGraphQL: mapResponse,
  trpcRefetchInterval: (row) =>
    row && row.Status !== ThirdPartyResponseStatus.Completed
      ? RESPONSE_POLL_INTERVAL_MS
      : false,
  graphqlDocument: TppGetResponseByIdDocument,
  graphqlVariables: ({ id }) => ({ Id: id }),
});

export const useTppResponseById = (id: string) =>
  useTppResponseByIdHook({ queryArgs: { id } });
